const ATTRIBUTION_STORAGE_KEY = 'scp_attribution';

const ATTRIBUTION_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'source',
  'article',
  'cta_location',
  'calculator',
  'fbclid',
  'gclid'
];

const safeParse = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (_) {
    return null;
  }
};

const isFacebookReferrer = (referrer) =>
  /facebook\.com|fb\.com|instagram\.com|m\.facebook\.com|lm\.facebook\.com|l\.facebook\.com/i.test(referrer || '');

const inferPaidSocialParams = (params, referrer) => {
  const inferred = {};
  const { fbclid, gclid } = params;
  if (fbclid || isFacebookReferrer(referrer)) {
    if (!params.utm_source) inferred.utm_source = 'facebook';
    if (!params.utm_medium) inferred.utm_medium = 'social';
    if (fbclid) inferred.fbclid = fbclid;
  }
  if (gclid && !params.utm_source) {
    inferred.utm_source = 'google';
    inferred.utm_medium = 'cpc';
    inferred.gclid = gclid;
  }
  return inferred;
};

const paramsFromLocation = () => {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search || '');
  if (window.location.hash && window.location.hash.includes('?')) {
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1]);
    hashParams.forEach((value, key) => {
      if (!params.has(key)) params.set(key, value);
    });
  }

  const extracted = {};
  ATTRIBUTION_PARAM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) extracted[key] = value;
  });

  return extracted;
};

export const captureAttribution = () => {
  if (typeof window === 'undefined') return null;

  const existing = safeParse(window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY)) || {};
  const params = paramsFromLocation();
  const referrer = document.referrer || '';
  const inferred = inferPaidSocialParams({ ...params }, referrer);
  const merged = { ...params, ...inferred };
  const hasParams = Object.keys(merged).length > 0;
  const now = new Date().toISOString();

  const latestTouch = {
    ...existing.latestTouch,
    ...merged,
    referrer: referrer || existing.latestTouch?.referrer || '',
    landing_path: `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`,
    captured_at: now
  };

  if (!latestTouch.referrer && isFacebookReferrer(referrer)) {
    latestTouch.inferred_referrer = referrer;
  }

  const attribution = {
    firstTouch: existing.firstTouch || latestTouch,
    latestTouch,
    updated_at: now
  };

  if (hasParams || !existing.latestTouch) {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  }

  return attribution;
};

export const getStoredAttribution = () => {
  if (typeof window === 'undefined') return null;
  return safeParse(window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY));
};

const pickTouch = (latest, first, key) => latest?.[key] || first?.[key] || '';

export const getCheckoutAttribution = () => {
  const attribution = getStoredAttribution() || captureAttribution();
  const latest = attribution?.latestTouch || {};
  const first = attribution?.firstTouch || {};

  return {
    utm_source: pickTouch(latest, first, 'utm_source'),
    utm_medium: pickTouch(latest, first, 'utm_medium'),
    utm_campaign: pickTouch(latest, first, 'utm_campaign'),
    utm_content: pickTouch(latest, first, 'utm_content'),
    utm_term: pickTouch(latest, first, 'utm_term'),
    source: pickTouch(latest, first, 'source'),
    article: pickTouch(latest, first, 'article'),
    cta_location: pickTouch(latest, first, 'cta_location'),
    fbclid: pickTouch(latest, first, 'fbclid'),
    gclid: pickTouch(latest, first, 'gclid'),
    referrer: pickTouch(latest, first, 'referrer') || pickTouch(latest, first, 'inferred_referrer'),
    landing_path: first.landing_path || '',
    latest_path: latest.landing_path || ''
  };
};

export const formatLeadReferrer = () => {
  const a = getCheckoutAttribution();
  if (a.referrer) return a.referrer;
  if (a.fbclid) return `facebook / social (fbclid:${String(a.fbclid).slice(0, 32)})`;
  if (a.utm_source) return `${a.utm_source} / ${a.utm_medium || 'unknown'}`;
  if (a.landing_path) return `direct (${a.landing_path})`;
  return 'direct';
};

export const getLeadCaptureFields = () => {
  const a = getCheckoutAttribution();
  return {
    userAgent: typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : 'unknown',
    referrer: formatLeadReferrer(),
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
    fbclid: a.fbclid,
    gclid: a.gclid,
    landing_path: a.landing_path
  };
};

export const getGa4ConfigParams = () => {
  const a = getCheckoutAttribution();
  const out = {};
  if (a.utm_source) out.campaign_source = a.utm_source;
  if (a.utm_medium) out.campaign_medium = a.utm_medium;
  if (a.utm_campaign) out.campaign_name = a.utm_campaign;
  if (a.utm_content) out.campaign_content = a.utm_content;
  else if (a.fbclid) out.campaign_content = String(a.fbclid).slice(0, 100);
  if (a.utm_term) out.campaign_term = a.utm_term;
  return out;
};

export const getGa4EventParams = () => {
  const a = getCheckoutAttribution();
  const out = {};
  if (a.utm_source) out.utm_source = a.utm_source;
  if (a.utm_medium) out.utm_medium = a.utm_medium;
  if (a.utm_campaign) out.utm_campaign = a.utm_campaign;
  if (a.fbclid) out.fbclid = String(a.fbclid).slice(0, 100);
  if (a.gclid) out.gclid = String(a.gclid).slice(0, 100);
  if (a.source) out.source = a.source;
  return out;
};
